import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Box, Heading } from '@99designs/emu';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { IconDown1, IconMinus2, IconPlus2, IconUp1 } from '@99designs/icons';
import { getElementHeight } from './helpers';

const StyledAccordionItem = styled(Box)`
    border-color: ${colors.grey300};
    border-style: solid;
    border-top-width: 1px;
    border-bottom-width: 0;
    border-left-width: 0;
    border-right-width: 0;

    &:last-of-type {
        border-bottom-width: 1px;
    }
`;

const ItemButton = styled.button`
    border: none;
    border-radius: 0;
    background: transparent;
    text-align: left;

    &:hover,
    &:focus {
        box-shadow: none;
        text-decoration: none;
        outline: none;
    }

    position: relative;
    width: 100%;
    padding-top: ${spaces['1.5bu']};
    padding-bottom: ${spaces['1.5bu']};
    padding-left: ${spaces['0.75bu']};
    padding-right: ${spaces['3bu']};
`;

const ItemIndicator = styled(Box)`
    position: absolute;
    right: ${spaces['0.75bu']};
    top: 50%;
    transform: translateY(-50%);
    &:before {
        display: block;
        margin: 0;
        line-height: 1;
    }
    font-size: ${typography.typeScale.size5}px;
    color: ${colors.grey500};
`;

const ItemBody = styled(Box)<{
    isOpen: boolean;
    ref: React.RefObject<HTMLDivElement>;
    bodyHeight: number;
}>(({ isOpen, bodyHeight }) => {
    return css`
        padding-left: ${spaces['0.75bu']};
        padding-right: ${spaces['0.75bu']};
        height: 0;
        min-height: 0;
        visibility: hidden;
        opacity: 0;
        overflow: hidden;
        transition: all 0.3s, visibility 0.3s;

        ${isOpen &&
        css`
            height: auto;
            min-height: ${bodyHeight}px;
            visibility: visible;
            opacity: 1;
        `}
    `;
});

export type AccordionIconType = 'plusMinus' | 'upDown';

export interface Props {
    itemKey: string;
    title: string;
    children: React.ReactNode;
    className?: string;
    onOpen?: () => void;
    iconType?: AccordionIconType;
}

export function AccordionItemLegacy({
    itemKey,
    title,
    children,
    className = '',
    onOpen,
    iconType = 'plusMinus',
}: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const elementId = `accordion-${itemKey}`;
    const [bodyHeight, setBodyHeight] = useState(0);
    const itemBodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = itemBodyRef.current;
        if (element) {
            setBodyHeight(getElementHeight(element));

            let timeoutId: ReturnType<typeof setTimeout>;

            const resizeListener = () => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    element && setBodyHeight(getElementHeight(element));
                }, 300);
            };

            window.addEventListener('resize', resizeListener);

            return () => {
                window.removeEventListener('resize', resizeListener);
            };
        }
        return;
    }, [itemBodyRef]);

    const getIconType = () => {
        switch (iconType) {
            case 'plusMinus':
                return isOpen ? <IconMinus2 /> : <IconPlus2 />;
            case 'upDown':
                return isOpen ? <IconUp1 /> : <IconDown1 />;
            default:
                return isOpen ? <IconMinus2 /> : <IconPlus2 />;
        }
    };

    return (
        <StyledAccordionItem data-testid="accordion-item" className={className}>
            <ItemButton
                onClick={() => {
                    if (!isOpen && onOpen) {
                        onOpen();
                    }
                    setIsOpen((isOpen) => !isOpen);
                }}
                aria-controls={`${elementId}-answer`}
                suppressHydrationWarning={true}
                aria-expanded={isOpen}
                id={`${elementId}-question`}
            >
                <Box>
                    <Heading appearanceLevel={5} accessibilityLevel={3}>
                        {title}
                    </Heading>
                </Box>
                <ItemIndicator>{getIconType()}</ItemIndicator>
            </ItemButton>
            <ItemBody
                aria-hidden={!isOpen}
                id={`${elementId}-answer`}
                isOpen={isOpen}
                ref={itemBodyRef}
                bodyHeight={bodyHeight}
            >
                {children}
            </ItemBody>
        </StyledAccordionItem>
    );
}
